import React from "react";
import { v4 } from 'uuid';
import BaseModel from './BaseModel';
import FormLayout from './FormLayout';
import FormLayoutFieldChoice from './FormLayoutFieldChoice';
import Utils from '../services/Utils';
import * as _ from 'es-toolkit';

/** Class representing a Form layout field. */
class FormLayoutField extends BaseModel {
    constructor(properties) {
        super(properties || {});

        if (!this.id)
            this.id = v4();

        if (this.choices?.length)
            this.choices = this.choices.map(choice => new FormLayoutFieldChoice(choice));

        if (this.group_id != null && (this.groupId === undefined || this.groupId === null)) {
            this.groupId = this.group_id;
        }
        if (this.groupId === undefined) {
            this.groupId = null;
        }
        delete this.group_id;

        if ((this.stepId === undefined || this.stepId === null) && this.step_id != null) {
            this.stepId = this.step_id;
        }
        delete this.step_id;
        if (this.stepId === undefined) {
            this.stepId = null;
        }
    }

    getType() {
        return this.type;
    }

    /**
     * Redux-form field name (root: field id; inside a group: dotted path).
     *
     * @param {{ reduxName?: string }|undefined} props
     * @returns {string}
     */
    reduxFieldName(props) {
        if (props?.reduxName != null && props.reduxName !== '') {
            return props.reduxName;
        }
        return this.id;
    }

    /**
     * Get this field default options
     */
    static getDefaultOptions() {
        return {
            name: '',
            groupId: null,
            stepId: null,
            label: {

            },
            description: {

            },
            width: 1,
            required: false,
            rows: 0,
            min: -Infinity,
            max: Infinity,
            step: 1,
            with_time: false,
            multiple: false,
            expanded: false,
            extensions: []
        };
    }

    /**
     * Create a new choice empty choice
     */
    static createChoice() {
        return {
            id: v4(),
            value: '',
            label: {

            }
        };
    }

    /**
     * Get choices refs, if any
     *
     * Refs are used to propose linked resources as choices,
     * like the sectors or abilities for example
     */
    getRefs() {
        return [];
    }

    /**
     * Get choices refs source, if any
     *
     * Refs are used to propose linked resources as choices,
     * like the sectors or abilities for example
     */
    getRefsSource() {
        return null;
    }

    /**
     * Return the list of options this field handles
     */
    getHandledOptions() {
        return [
            FormLayout.OPTION_WIDTH,
            FormLayout.OPTION_NAME,
            FormLayout.OPTION_LABEL,
            FormLayout.OPTION_DESCRIPTION,
            FormLayout.OPTION_REQUIRED,
        ];
    }

    /**
     * Initial redux-form value for this field (e.g. defaults from content.defaults).
     * @param {object} [_resources] Optional form resources (sectors, …) for resolution.
     * @returns {unknown|undefined}
     */
    getFormInitialValue(_resources) {
        return undefined;
    }

    /**
     * Check if this field handles the provided option
     */
    handleOption(option) {
        return this.getHandledOptions().includes(option);
    }

    /**
     * Native HTML `required` for inputs. Disabled in the layout editor preview
     * so saving layout metadata is not blocked by empty preview fields.
     */
    htmlRequired(props) {
        return !!this.required && !props?.inEdition && !props?.inPreview;
    }

    /**
     * Return this field preview as JSX string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getPreview(_props) {
        return <div>{this.type}</div>;
    }

    /**
     * Return this field preview input
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getPreviewInput(props) {
        return this.getInput(props);
    }

    /**
     * Return this field preview label
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     */
    getPreviewLabel(props) {
        return this.getLabel(props);
    }

    /**
     * Render this field input
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getInput(props) {
        return (
            <input className="form-control" type="text" name={this.name} required={this.htmlRequired(props)} />
        );
    }

    /**
     * Render this field label
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     */
    getLabel(props) {
        const raw = props?.lang;
        const uiLang = Utils.i18nUiLang({
            language: raw,
            resolvedLanguage: raw,
        });
        const text = Utils.localized_or_fallback(this.label, uiLang, '');
        return (
            <label className={!!this.required ? 'required' : ''}>
                { text }
            </label>
        );
    }

    /**
     * Render this field description (between label and input).
     * Returns null when empty so callers can skip the layout slot.
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     */
    getDescription(props) {
        const raw = props?.lang;
        const uiLang = Utils.i18nUiLang({
            language: raw,
            resolvedLanguage: raw,
        });
        const text = Utils.localized_or_fallback(this.description, uiLang, '');
        if (!text) {
            return null;
        }
        return (
            <small className="form-text text-muted mt-0 mb-1">
                { text }
            </small>
        );
    }

    /**
     * Render a value
     *
     * @param {Any} value
     * @param {Object} context: an object with the following keys:
     *                   - lang (mandatory)
     *                   - t (mandatory)
     *                   - format (optional)
     *
     * @return {String}
     */
    renderValue(value, context) {
        if (_.isArray(value))
            return value.map(v => this.renderValue(v, context)).join(', ');

        if (!_.isObject(value))
            // convert scalar values to string
            return '' + value;

        return Utils.localized_or_fallback(value, context?.lang, '');
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'cube';
    }

    /**
     * Is this field resizable ?
     **/
    isResizable = () => false;

    /**
     * Is this field resizable only horizontally ?
     **/
    isResizableHorizontallyOnly = () => false;

    /**
     * Is this field editable ?
     **/
    isEditable = () => true;

    /**
     * Set one of this field option
     */
    setOption = (option, value) => {
        this[option] = value;
    };

    /**
     * Convert this field instance to a plain object
     */
    toPlainObject = () => JSON.parse(JSON.stringify(this));
}

export default FormLayoutField;
